import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import InventoryTable from "../components/admin/inventory/InventoryTable";
import InventoryGrid from "../components/admin/inventory/InventoryGrid";
import SearchInput from "../components/CommonComponents/SearchInput";
import ViewToggle from "../components/CommonComponents/ViewToggle";
import EmptyState from "../components/CommonComponents/EmptyState";
import ProductImagePreviewModal from "../components/admin/products/ProductImagePreviewModal";
import { getInventory, updateInventory } from "../services/inventoryServices";
import { fetchBranches } from "../services/branchApi";
import { showSuccessToast, showErrorToast } from "../utils/toast";
import { Building2, ChevronDown, Package, Search, Warehouse } from "lucide-react";
import EditStockModal from "../components/admin/inventory/EditStockModal";

const InventorySkeleton = ({ viewMode = "table" }) => (
    <div className="animate-pulse space-y-6">
        <div className="rounded-[28px] border border-white/60 bg-[linear-gradient(135deg,rgba(255,255,255,0.95),rgba(248,250,252,0.92))] p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] sm:p-6">
            <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-3">
                    <div className="h-4 w-32 rounded-full bg-slate-200" />
                    <div className="h-10 w-60 rounded-2xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200" />
                    <div className="h-4 w-72 rounded-full bg-slate-100" />
                </div>
                <div className="h-12 w-full max-w-md rounded-2xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200" />
            </div>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, idx) => (
                    <div key={idx} className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
                        <div className="h-3 w-20 rounded-full bg-slate-100" />
                        <div className="mt-3 h-7 w-16 rounded-full bg-slate-200" />
                    </div>
                ))}
            </div>
        </div>

        <div className="rounded-[26px] border border-slate-200 bg-white/85 p-3 shadow-sm">
            <div className="flex gap-2 overflow-hidden">
                {Array.from({ length: 4 }).map((_, idx) => (
                    <div key={idx} className="h-11 w-32 rounded-2xl bg-slate-100" />
                ))}
            </div>
        </div>

        {viewMode === "table" ? (
            <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white/90 shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
                <div className="grid grid-cols-[minmax(240px,2.4fr),120px,140px,140px,120px] gap-4 border-b border-slate-200 bg-slate-100/90 px-6 py-4">
                    {Array.from({ length: 5 }).map((_, idx) => (
                        <div key={idx} className="h-3.5 rounded-full bg-slate-200" />
                    ))}
                </div>
                <div className="divide-y divide-slate-100">
                    {Array.from({ length: 6 }).map((_, rowIdx) => (
                        <div key={rowIdx} className="grid grid-cols-[minmax(240px,2.4fr),120px,140px,140px,120px] items-center gap-4 px-6 py-5">
                            <div className="flex items-center gap-4">
                                <div className="h-12 w-12 rounded-2xl bg-slate-100" />
                                <div className="min-w-0 flex-1 space-y-2">
                                    <div className="h-4 w-40 rounded-full bg-slate-200" />
                                    <div className="h-3 w-56 max-w-full rounded-full bg-slate-100" />
                                </div>
                            </div>
                            <div className="flex justify-center">
                                <div className="h-9 w-20 rounded-full bg-slate-100" />
                            </div>
                            <div className="flex justify-center">
                                <div className="h-10 w-24 rounded-2xl bg-emerald-50" />
                            </div>
                            <div className="flex justify-center">
                                <div className="h-10 w-24 rounded-2xl bg-amber-50" />
                            </div>
                            <div className="flex justify-center gap-2">
                                <div className="h-8 w-8 rounded-xl bg-slate-100" />
                                <div className="h-8 w-8 rounded-xl bg-slate-200" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
                {Array.from({ length: 12 }).map((_, idx) => (
                    <div key={idx} className="overflow-hidden rounded-[26px] border border-slate-200 bg-white/90 p-4 shadow-[0_18px_40px_rgba(15,23,42,0.07)]">
                        <div className="relative overflow-hidden rounded-2xl bg-slate-100">
                            <div className="h-32 w-full bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100" />
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="h-4 w-4/5 rounded-full bg-slate-200" />
                            <div className="h-3 w-1/2 rounded-full bg-slate-100" />
                            <div className="h-6 rounded-full bg-emerald-50" />
                        </div>
                        <div className="mt-4 flex justify-end">
                            <div className="h-8 w-8 rounded-xl bg-slate-200" />
                        </div>
                    </div>
                ))}
            </div>
        )}

        <div className="mt-4 flex flex-col items-center gap-3">
            <div className="h-9 w-56 rounded-xl bg-slate-100" />
            <div className="h-4 w-60 rounded-full bg-slate-100" />
        </div>
    </div>
);

const InventoryPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [inventories, setInventories] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [branches, setBranches] = useState([]);
    const [selectedBranchId, setSelectedBranchId] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState("table");
    const [showSummaryCards, setShowSummaryCards] = useState(true);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedInventory, setSelectedInventory] = useState(null);
    const [previewProduct, setPreviewProduct] = useState(null);
    const inventoriesPerPage = 10;
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const customerId = sessionStorage.getItem("customer_id") || localStorage.getItem("customer_id");
    const uploadsBaseURL = `${process.env.REACT_APP_API_URL}/uploads`;
    const notificationProductId = searchParams.get("productId");
    const notificationProductName = searchParams.get("productName");

    const branchTabs = useMemo(() => ([
        { id: "", label: "Warehouse", type: "warehouse" },
        ...branches.map((branch) => ({
            id: String(branch.id),
            label: branch.name || branch.location || `Branch ${branch.id}`,
            type: "branch",
        })),
    ]), [branches]);
    const shouldShowBranchTabs = branches.length > 0;
    const hasNoActiveBranches = branches.length === 0;

    const loadInventory = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getInventory(token, selectedBranchId);
            setInventories(data || []);
        } catch (err) {
            console.error(err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [token, selectedBranchId]);

    const loadBranches = useCallback(async () => {
        try {
            const res = await fetchBranches(customerId);
            const branchList = res.data?.data || res.data || [];
            setBranches(
                Array.isArray(branchList)
                    ? branchList.filter((branch) => String(branch?.status || "").toUpperCase() === "ACTIVE")
                    : []
            );
        } catch (err) {
            console.error(err);
            setBranches([]);
        }
    }, [customerId]);

    const handleAdjust = useCallback(async (productId, change) => {
        if (!token) return showErrorToast("JWT token missing. Please login.");
        try {
            await updateInventory(productId, change, token, selectedBranchId);
            showSuccessToast(`Inventory ${change > 0 ? "restocked" : "deducted"}`);
            loadInventory();
        } catch (err) {
            console.error(err);
            showErrorToast(err.message || "Failed to update inventory");
        }
    }, [token, selectedBranchId, loadInventory]);

    const filteredInventory = inventories.filter((inv) =>
        inv.product_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const inventorySuggestions = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        if (!normalizedSearch) return [];
        const uniqueNames = new Set();

        return inventories
            .map((inv) => inv.product_name)
            .filter((name) => {
                if (!name || uniqueNames.has(name)) return false;
                const matches = name.toLowerCase().includes(normalizedSearch);
                if (matches) {
                    uniqueNames.add(name);
                }
                return matches;
            })
            .slice(0, 6);
    }, [inventories, searchTerm]);
    const totalPages = Math.max(Math.ceil(filteredInventory.length / inventoriesPerPage), 1);
    const currentInventory = filteredInventory.slice(
        (currentPage - 1) * inventoriesPerPage,
        currentPage * inventoriesPerPage
    );
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * inventoriesPerPage;
    const activeBranchLabel = hasNoActiveBranches
        ? "No Branch is Active"
        : selectedBranchId
        ? branchTabs.find((tab) => tab.id === selectedBranchId)?.label || "Selected Branch"
        : "Warehouse";
    const totalAvailableStock = filteredInventory.reduce((sum, inv) => sum + (Number(inv.quantity) || 0), 0);
    const totalReleasedStock = hasNoActiveBranches
        ? 0
        : filteredInventory.reduce((sum, inv) => sum + (Number(inv.released_quantity) || 0), 0);

    useEffect(() => { loadBranches(); }, [loadBranches]);
    useEffect(() => { loadInventory(); }, [loadInventory]);
    useEffect(() => { if (currentPage > totalPages) setCurrentPage(1); }, [filteredInventory, totalPages, currentPage]);
    useEffect(() => { setCurrentPage(1); }, [selectedBranchId]);
    useEffect(() => {
        if (branches.length > 0) {
            const hasSelectedBranch = branches.some((branch) => String(branch.id) === String(selectedBranchId));
            if (selectedBranchId && !hasSelectedBranch) {
                setSelectedBranchId("");
            }
        }

        if (branches.length === 0) {
            if (selectedBranchId) {
                setSelectedBranchId("");
            }
        }
    }, [branches, selectedBranchId]);

    useEffect(() => {
        if (!notificationProductId && !notificationProductName) return;

        const normalizedParamName = notificationProductName?.trim() || "";
        const matchedInventory = notificationProductId
            ? inventories.find((inv) => String(inv.id) === String(notificationProductId) || String(inv.product_id) === String(notificationProductId))
            : null;
        const nextSearchTerm = normalizedParamName || matchedInventory?.product_name || "";

        if (nextSearchTerm) {
            setSearchTerm((prev) => (prev === nextSearchTerm ? prev : nextSearchTerm));
            setCurrentPage(1);
        }

        if (notificationProductId || notificationProductName) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete("productId");
            nextParams.delete("productName");
            setSearchParams(nextParams, { replace: true });
        }
    }, [inventories, notificationProductId, notificationProductName, searchParams, setSearchParams]);

    const handleEditStock = useCallback(async (inventory, newQuantity) => {
        if (!token) return showErrorToast("JWT token missing. Please login.");
        try {
            const currentScopedQuantity = selectedBranchId
                ? (Number(inventory.released_quantity) || 0)
                : (Number(inventory.quantity) || 0);
            const quantityChange = newQuantity - currentScopedQuantity;
            if (quantityChange === 0) return;
            await updateInventory(inventory.id, quantityChange, token, selectedBranchId);
            showSuccessToast("Inventory updated");
            loadInventory();
        } catch (err) {
            console.error(err);
            showErrorToast(err.message || "Failed to update inventory");
        }
    }, [token, selectedBranchId, loadInventory]);

    const handleSaveStock = (newQuantity) => {
        if (selectedInventory) {
            handleEditStock(selectedInventory, newQuantity);
        }
        setEditModalOpen(false);
        setSelectedInventory(null);
    };

    const handleCloseModal = () => {
        setEditModalOpen(false);
        setSelectedInventory(null);
    };

    return (
        <div className="pt-3">
            <div className="flex flex-col gap-4">
                <section className="relative overflow-hidden rounded-[30px] border border-slate-200/85 bg-[linear-gradient(145deg,#fffafa,#fffdfd,#ffffff)] p-5 shadow-[0_24px_80px_rgba(15,23,42,0.08)] sm:p-6">
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full blur-3xl" style={{ background: "color-mix(in srgb, var(--color-primary) 18%, transparent)" }} />
                        <div className="absolute left-10 top-0 h-28 w-28 rounded-full bg-white/60 blur-2xl" />
                    </div>

                    <div className="relative flex flex-col gap-4">
                        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                            <div className="min-w-0">
                                <div className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 shadow-sm">
                                    <Warehouse className="h-3.5 w-3.5" />
                                    Inventory Workspace
                                </div>
                                <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                                    Inventory List
                                </h1>
                                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500 sm:text-[15px]">
                                    Monitor warehouse stock, review released quantities by branch, and update inventory from one refined workspace.
                                </p>
                            </div>

                            <div className="w-full max-w-xl">
                                <SearchInput
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    onSuggestionSelect={(productName) => {
                                        setSearchTerm(productName);
                                        setCurrentPage(1);
                                    }}
                                    suggestions={inventorySuggestions}
                                    placeholder="Search by product name..."
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 rounded-[22px] border border-white/70 bg-white/80 px-4 py-3 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                            <div>
                                <p className="text-sm font-semibold text-slate-900">Inventory Summary</p>
                                <p className="mt-1 text-xs text-slate-500">Show or hide the Products, Available, Released, and Scope cards.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowSummaryCards((prev) => !prev)}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
                                aria-expanded={showSummaryCards}
                                aria-label={showSummaryCards ? "Hide inventory summary cards" : "Show inventory summary cards"}
                            >
                                {showSummaryCards ? "Hide" : "Show"}
                                <ChevronDown className={`h-4 w-4 transition ${showSummaryCards ? "rotate-180" : "rotate-0"}`} />
                            </button>
                        </div>

                        {showSummaryCards && (
                            <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
                                {loading ? (
                                    Array.from({ length: 4 }).map((_, idx) => (
                                        <div key={idx} className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                                            <div className="h-3 w-20 rounded-full bg-slate-100" />
                                            <div className="mt-3 h-8 w-16 rounded-full bg-slate-200" />
                                            <div className="mt-2 h-3 w-32 rounded-full bg-slate-100" />
                                        </div>
                                    ))
                                ) : (
                                    <>
                                        <div className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                                <Package className="h-4 w-4" />
                                                Products
                                            </div>
                                            <p className="mt-3 text-2xl font-semibold text-slate-900">{filteredInventory.length}</p>
                                            <p className="mt-1 text-xs text-slate-500">Items in the current filtered view</p>
                                        </div>
                                        <div className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                                <Warehouse className="h-4 w-4" />
                                                Available
                                            </div>
                                            <p className="mt-3 text-2xl font-semibold text-slate-900">{totalAvailableStock}</p>
                                            <p className="mt-1 text-xs text-slate-500">Warehouse stock across the current list</p>
                                        </div>
                                        <div className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                                <Building2 className="h-4 w-4" />
                                                Released
                                            </div>
                                            <p className="mt-3 text-2xl font-semibold text-slate-900">{totalReleasedStock}</p>
                                            <p className="mt-1 text-xs text-slate-500">
                                                {shouldShowBranchTabs
                                                    ? "Released inventory in the current branch view"
                                                    : "Released inventory for the active branch"}
                                            </p>
                                        </div>
                                        <div className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                                <Search className="h-4 w-4" />
                                                Scope
                                            </div>
                                            <p className={`mt-3 truncate text-xl font-semibold ${hasNoActiveBranches ? "text-rose-600" : "text-slate-900"}`}>
                                                {activeBranchLabel}
                                            </p>
                                            <p className={`mt-1 text-xs ${hasNoActiveBranches ? "text-rose-500" : "text-slate-500"}`}>
                                                {hasNoActiveBranches
                                                    ? "Activate at least one branch to view released inventory."
                                                    : (shouldShowBranchTabs ? "Active inventory view" : "Current active branch")}
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </section>

                <section className="rounded-[30px] border border-slate-200/80 bg-white/80 p-4 shadow-[0_18px_60px_rgba(15,23,42,0.06)] backdrop-blur-sm sm:p-5">
                    <div className="flex items-start justify-between gap-4">
                        {shouldShowBranchTabs ? (
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-slate-900">Inventory Scope</p>
                                <p className="mt-1 text-xs text-slate-500">
                                    Switch between warehouse stock and branch-specific released stock without leaving the page.
                                </p>
                            </div>
                        ) : (
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-slate-900">Inventory Layout</p>
                                <p className="mt-1 text-xs text-slate-500">
                                    Review inventory using the layout that feels best for the current screen.
                                </p>
                            </div>
                        )}
                        <div className="shrink-0">
                            <ViewToggle
                                viewMode={viewMode}
                                onToggle={() => setViewMode(viewMode === "table" ? "grid" : "table")}
                            />
                        </div>
                    </div>

                    {shouldShowBranchTabs ? (
                        <div className="mt-4 overflow-x-auto">
                            <div className="inline-flex min-w-full gap-2 rounded-[22px] border border-slate-200 bg-slate-50/85 p-1.5 shadow-inner shadow-white/40">
                                {branchTabs.map((branchTab) => {
                                    const isActive = selectedBranchId === branchTab.id;
                                    return (
                                        <button
                                            key={branchTab.id || "all-branches"}
                                            type="button"
                                            onClick={() => setSelectedBranchId(branchTab.id)}
                                            className={`rounded-[18px] px-4 py-1.5 text-left transition ${isActive
                                                ? "text-white shadow-[0_14px_30px_rgba(15,23,42,0.16)]"
                                                : "text-slate-600 hover:bg-white hover:text-slate-900"
                                                }`}
                                            style={isActive ? { background: "linear-gradient(135deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 76%, black))" } : undefined}
                                        >
                                            {branchTab.type === "warehouse" ? (
                                                <span className={`block whitespace-nowrap text-sm font-semibold ${isActive ? "text-white" : ""}`}>
                                                    {branchTab.label}
                                                </span>
                                            ) : (
                                                <span className="block min-w-[96px]">
                                                    <span className={`block text-[9px] font-semibold uppercase tracking-[0.18em] leading-none ${isActive ? "text-white/75" : "text-slate-400"}`}>
                                                        Branch
                                                    </span>
                                                    <span className={`mt-0.5 block whitespace-nowrap text-sm font-semibold leading-tight ${isActive ? "text-white" : "text-slate-900"}`}>
                                                        {branchTab.label}
                                                    </span>
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                </section>

                <section className="p-0">
                    {error ? (
                        <EmptyState
                            icon={Package}
                            title="Failed to Load Inventory"
                            message="Check your internet connection and try again."
                            onRetry={loadInventory}
                            retryLabel="Reload"
                        />
                    ) : loading ? (
                        <InventorySkeleton viewMode={viewMode} />
                    ) : currentInventory.length === 0 ? (
                        <EmptyState
                            icon={Package}
                            title="No Inventory Found"
                            message={searchTerm ? "No inventory matches your search." : "No inventory available yet."}
                        />
                    ) : viewMode === "table" ? (
                        <InventoryTable
                            inventories={currentInventory}
                            onAdjust={handleAdjust}
                            uploadsBaseURL={uploadsBaseURL}
                            onEditStock={handleEditStock}
                            selectedBranchId={selectedBranchId}
                            hideActions={hasNoActiveBranches}
                            onPreviewImage={setPreviewProduct}
                        />
                    ) : (
                        <InventoryGrid
                            inventories={currentInventory}
                            onAdjust={handleAdjust}
                            uploadsBaseURL={uploadsBaseURL}
                            selectedBranchId={selectedBranchId}
                            onEdit={(inv) => {
                                setSelectedInventory(inv);
                                setEditModalOpen(true);
                            }}
                        />
                    )}
                </section>

                {!error && (
                    <div className="flex items-center justify-between gap-3 overflow-x-auto pb-10 text-xs whitespace-nowrap">
                        <div className="shrink-0 text-gray-500">
                            Showing {filteredInventory.length === 0 ? 0 : startIndex + 1}
                            -{Math.min(startIndex + inventoriesPerPage, filteredInventory.length)} of {filteredInventory.length}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            <button
                                type="button"
                                className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                                disabled={safePage === 1}
                            >
                                Prev
                            </button>
                            <div className="text-xs text-gray-600">
                                Page {safePage} of {totalPages}
                            </div>
                            <button
                                type="button"
                                className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                                disabled={safePage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <EditStockModal
                isOpen={editModalOpen}
                onClose={handleCloseModal}
                inventory={selectedInventory}
                onSave={handleSaveStock}
                selectedBranchId={selectedBranchId}
            />
            <ProductImagePreviewModal
                open={Boolean(previewProduct)}
                onClose={() => setPreviewProduct(null)}
                product={previewProduct ? {
                    ...previewProduct,
                    name: previewProduct.product_name || previewProduct.name,
                } : null}
            />
        </div>
    );
};

export default InventoryPage;
